/**
 * Belief inspector API service (SPEC §2.6 item 7).
 *
 * Pin / correct / retire flow through the backend BeliefService — they
 * record human evidence and keep the retest floor.
 */

import type {
  BeliefActionResponse,
  BeliefDetail,
  BeliefsListResponse,
  BeliefStatus,
} from '../types/beliefs';
import type { AgentDomain } from '../types/agentSettings';
import { createHeaders } from './apiClient';
import { handleResponse } from './httpResponse';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface BeliefsFilters {
  domain?: AgentDomain;
  status?: BeliefStatus;
  limit?: number;
  offset?: number;
}

export async function listBeliefs(
  filters: BeliefsFilters = {}
): Promise<BeliefsListResponse> {
  const params = new URLSearchParams();
  if (filters.domain) params.append('domain', filters.domain);
  if (filters.status) params.append('status', filters.status);
  if (filters.limit !== undefined) params.append('limit', String(filters.limit));
  if (filters.offset !== undefined) params.append('offset', String(filters.offset));
  const qs = params.toString();

  const response = await fetch(
    `${API_BASE_URL}/api/beliefs${qs ? `?${qs}` : ''}`,
    { method: 'GET', headers: createHeaders() }
  );
  return handleResponse<BeliefsListResponse>(response);
}

export async function getBelief(beliefId: string): Promise<BeliefDetail> {
  const response = await fetch(`${API_BASE_URL}/api/beliefs/${beliefId}`, {
    method: 'GET',
    headers: createHeaders(),
  });
  return handleResponse<BeliefDetail>(response);
}

export async function pinBelief(
  beliefId: string,
  confidence: number,
  statement?: string
): Promise<BeliefActionResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/beliefs/${beliefId}/pin`,
    {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify({ confidence, statement }),
    }
  );
  return handleResponse<BeliefActionResponse>(response);
}

export async function correctBelief(
  beliefId: string,
  update: { statement?: string; confidence?: number }
): Promise<BeliefActionResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/beliefs/${beliefId}/correct`,
    {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(update),
    }
  );
  return handleResponse<BeliefActionResponse>(response);
}

export async function retireBelief(
  beliefId: string
): Promise<BeliefActionResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/beliefs/${beliefId}/retire`,
    { method: 'POST', headers: createHeaders() }
  );
  return handleResponse<BeliefActionResponse>(response);
}
